import React, { useState, useEffect, useCallback } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
export function SearchableSelectLazy({ 
  apiEndpoint,
  value, 
  onChange, 
  placeholder = "Select...",
  searchPlaceholder = "Type to search...",
  className = "",
  dataTestId = "",
  minSearchLength = 2
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalAvailable, setTotalAvailable] = useState(0);

  // Debounced search function
  const searchOptions = useCallback(
    async (query) => {
      if (query.length < minSearchLength) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const response = await api.get(`${apiEndpoint}?search=${encodeURIComponent(query)}&limit=100`);
        setOptions(response.data.municipalities || response.data.results || []);
        if (response.data.total_available) {
          setTotalAvailable(response.data.total_available);
        }
      } catch (error) {
        console.error('Error fetching options:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [apiEndpoint, minSearchLength]
  );

  // Debounce the search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchOptions(searchQuery);
      } else {
        setOptions([]);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery, searchOptions]);

  const handleSelect = (selectedValue) => {
    onChange(selectedValue);
    setOpen(false);
    setSearchQuery("");
    setOptions([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between input-field ${className}`}
          data-testid={dataTestId}
        >
          <span className={value ? "text-slate-900" : "text-slate-500"}>
            {value || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" style={{ width: 'var(--radix-popover-trigger-width)' }}>
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder={searchPlaceholder}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              {searchQuery.length < minSearchLength ? (
                <div className="py-6 text-center text-sm text-slate-500">
                  Type at least {minSearchLength} characters to search
                  {totalAvailable > 0 && (
                    <div className="text-xs text-slate-400 mt-1">
                      {totalAvailable.toLocaleString()} options available
                    </div>
                  )}
                </div>
              ) : options.length === 0 ? (
                <CommandEmpty>No results found.</CommandEmpty>
              ) : (
                <>
                  <CommandGroup className="max-h-64 overflow-y-auto">
                    {options.map((option) => (
                      <CommandItem
                        key={option}
                        value={option}
                        onSelect={() => handleSelect(option)}
                      >
                        <Check
                          className={`mr-2 h-4 w-4 ${
                            value === option ? "opacity-100" : "opacity-0"
                          }`}
                        />
                        {option}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  {options.length >= 100 && (
                    <div className="border-t p-2 text-xs text-center text-slate-500">
                      Showing first 100 results. Type more to refine search.
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
